import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { acceptHouseholdInvitation } from '../../api/household';
import { translations } from '../../locales/pl';
import { styles } from './AcceptHouseholdInvitation.styles';

type AcceptanceStatus = 'loading' | 'success' | 'error' | 'user_doesnt_exist' | 'user_invitation_invalid' | 'user_already_member';

const AcceptHouseholdInvitation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<AcceptanceStatus>('loading');

  useEffect(() => {
    const acceptInvitation = async () => {
      if (!id) {
        setStatus('error');
        return;
      }

      try {
        await acceptHouseholdInvitation(id);
        setStatus('success');
      } catch (error) {
        if (error instanceof Error) {
          const errorMessage = error.message;

          if (errorMessage === 'user_doesnt_exist') {
            setStatus('user_doesnt_exist');
          } else if (errorMessage === 'user_invitation_invalid') {
            setStatus('user_invitation_invalid');
          } else if (errorMessage === 'user_already_member') {
            setStatus('user_already_member');
          } else {
            setStatus('error');
          }
        } else {
          setStatus('error');
        }
      }
    };

    acceptInvitation();
  }, [id]);

  const handleLoginRedirect = () => {
    navigate('/');
  };

  const handleRegisterRedirect = () => {
    navigate('/register');
  };

  return (
    <Container maxWidth="sm">
      <Box sx={styles.container}>
        {status === 'loading' && (
          <Box sx={styles.contentBox}>
            <CircularProgress size={80} sx={styles.loader} />
            <Typography variant="h5" sx={styles.title}>
              {translations.acceptInvitation.accepting}
            </Typography>
          </Box>
        )}

        {status === 'success' && (
          <Box sx={styles.contentBox}>
            <CheckCircleOutlineIcon sx={styles.successIcon} />
            <Typography variant="h4" sx={styles.title}>
              {translations.acceptInvitation.success}
            </Typography>
            <Typography variant="body1" sx={styles.description}>
              {translations.acceptInvitation.successDescription}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleLoginRedirect}
              sx={styles.button}
            >
              {translations.acceptInvitation.loginButton}
            </Button>
          </Box>
        )}

        {status === 'user_doesnt_exist' && (
          <Box sx={styles.contentBox}>
            <WarningAmberIcon sx={styles.warningIcon} />
            <Typography variant="h4" sx={styles.title}>
              {translations.acceptInvitation.userDoesntExist}
            </Typography>
            <Typography variant="body1" sx={styles.description}>
              {translations.acceptInvitation.userDoesntExistDescription}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleRegisterRedirect}
              sx={styles.button}
            >
              {translations.acceptInvitation.registerButton}
            </Button>
          </Box>
        )}

        {status === 'user_invitation_invalid' && (
          <Box sx={styles.contentBox}>
            <ErrorOutlineIcon sx={styles.errorIcon} />
            <Typography variant="h4" sx={styles.title}>
              {translations.acceptInvitation.invitationInvalid}
            </Typography>
            <Typography variant="body1" sx={styles.description}>
              {translations.acceptInvitation.invitationInvalidDescription}
            </Typography>
          </Box>
        )}

        {status === 'user_already_member' && (
          <Box sx={styles.contentBox}>
            <WarningAmberIcon sx={styles.warningIcon} />
            <Typography variant="h4" sx={styles.title}>
              {translations.acceptInvitation.userAlreadyMember}
            </Typography>
            <Typography variant="body1" sx={styles.description}>
              {translations.acceptInvitation.userAlreadyMemberDescription}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleLoginRedirect}
              sx={styles.button}
            >
              {translations.acceptInvitation.loginButton}
            </Button>
          </Box>
        )}

        {status === 'error' && (
          <Box sx={styles.contentBox}>
            <ErrorOutlineIcon sx={styles.errorIcon} />
            <Typography variant="h4" sx={styles.title}>
              {translations.acceptInvitation.error}
            </Typography>
            <Typography variant="body1" sx={styles.description}>
              {translations.acceptInvitation.invitationInvalidDescription}
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default AcceptHouseholdInvitation;
